import Image from "next/image";
import React from "react";

export interface ComparisonTableV2Header {
    competitorName: string;
    featureLabel: string;
    gallaboxImage: string;
    competitorImage: string;
}

export type ComparisonTableV2Icon = "checkmark" | "forbidden" | "xmark" | "warn";

export interface ComparisonTableV2Data {
    feature: string;
    gallabox: string | boolean;
    competitor: string | boolean;
    gallaboxIcon?: ComparisonTableV2Icon;
    competitorIcon?: ComparisonTableV2Icon;
}

export interface ComparisonTableV2Props {
    title: string;
    description: string | JSX.Element;
    tableHeader: ComparisonTableV2Header;
    tableData: ComparisonTableV2Data[];
}

const renderCell = (value: string | boolean, icon?: ComparisonTableV2Icon) => {
    return (
        <div className="flex items-start gap-2">
            {icon === "checkmark" && <span>✅</span>}
            {icon === "forbidden" && <span>🚫</span>}
            {icon === "xmark" && <span>❌</span>}
            {icon === "warn" && <span>⚠</span>}
            {value && <p className="my-auto" dangerouslySetInnerHTML={{ __html: value }} />}
        </div>
    );
};

export const ComparisonTableV2: React.FC<ComparisonTableV2Props> = ({ title, description, tableHeader, tableData }) => {
    return (
        <>
            {/* Desktop View */}
            <div className="py-20 md:block hidden font-inter text-black-900" style={{ background: "linear-gradient(144.1deg, #3364FA 0.62%, #001C73 101.04%)" }}>
                <h5 className="max-w-4xl mx-auto text-3xl  md:text-5xl text-center mb-5 px-5 text-white font-bold" style={{ fontFamily: "Degular Variable" }}>
                    {title}
                </h5>

                <p className="font-inter text-center text-white font-xl mb-8 max-w-3xl mx-auto">{description}</p>
                <p className="text-center text-white font-semibold font-inter text-md">FEATURE COMPARISON</p>

                <div className="flex items-center justify-center mt-2 mb-6">
                    <svg width="6" height="28" viewBox="0 0 6 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.89062 28L5.77738 23H0.00387359L2.89062 28ZM2.89062 0H2.39062V23.5H2.89062H3.39062V0H2.89062Z" fill="#C6D4FF" />
                    </svg>
                </div>

                <div
                    className="grid grid-cols-3 md:max-w-6xl md:mx-auto border-t border-r border-l border-solid rounded-t-xl border-gray-100 bg-white"
                    style={{ maxHeight: "120px" }}
                >
                    <div className="flex p-6 bg-gray-50 rounded-tl-xl">
                        <p className="my-auto font-bold text-xl">{tableHeader.featureLabel}</p>
                    </div>
                    <div className="bg-white border-r border-solid border-gray-100 p-6">
                        <Image src={tableHeader.gallaboxImage} width={120} height={50} alt="Gallabox" />
                    </div>
                    <div className="bg-gray-50 p-6 rounded-tr-xl">
                        <Image
                            src={tableHeader.competitorImage}
                            width={120}
                            height={60}
                            alt={`Competitor ${tableHeader.competitorName}`}
                            style={{ height: "35px", objectFit: "contain" }}
                        />
                    </div>
                </div>
                {tableData.map((data, ind) => (
                    <div className={`grid grid-cols-3 md:max-w-6xl md:mx-auto border border-solid border-gray-100 ${ind === tableData.length - 1 ? "rounded-b-xl" : ""}`} key={ind}>
                        <div className={`flex p-5 bg-gray-50 ${ind === tableData.length - 1 ? "rounded-bl-xl" : ""}`}>
                            <p className="my-auto">{data.feature}</p>
                        </div>
                        <div className="flex bg border-r border-solid border-gray-100 p-5 justify-start items-start bg-white">{renderCell(data.gallabox, data.gallaboxIcon)}</div>
                        <div className={`flex bg-gray-50 p-5 justify-start items-start ${ind === tableData.length - 1 ? "rounded-br-xl" : ""}`}>
                            {renderCell(data.competitor, data.competitorIcon)}
                        </div>
                    </div>
                ))}
            </div>

            {/* Mobile View */}
            <section className="md:hidden block py-20" style={{ background: "linear-gradient(144.1deg, #3364FA 0.62%, #001C73 101.04%)" }}>
                <h5 className="max-w-4xl mx-auto text-3xl  md:text-5xl text-center mb-5 px-5 text-white font-bold" style={{ fontFamily: "Degular Variable" }}>
                    {title}
                </h5>

                <p className="font-inter text-center text-white font-xl mb-8 max-w-3xl mx-auto">{description}</p>
                <p className="text-center text-white font-semibold font-inter text-md">FEATURE COMPARISON</p>

                <div className="flex items-center justify-center mt-2 mb-6">
                    <svg width="6" height="28" viewBox="0 0 6 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.89062 28L5.77738 23H0.00387359L2.89062 28ZM2.89062 0H2.39062V23.5H2.89062H3.39062V0H2.89062Z" fill="#C6D4FF" />
                    </svg>
                </div>
                <div className="grid grid-cols-2 items-center border border-solid border-gray-300 mx-5 bg-white">
                    <div className="flex p-3 justify-center border-r border-solid border-gray-300">
                        <Image src={tableHeader.gallaboxImage} width={100} height={20} alt="Gallabox" />
                    </div>
                    <div className="flex p-3 justify-center">
                        <Image src={tableHeader.competitorImage} width={100} height={20} alt="Competitor" />
                    </div>
                </div>
                <div>
                    {tableData.map((data, ind) => (
                        <div className="mx-5" key={ind}>
                            <div className="bg-blue-50 border border-gray-300 border-solid font-semibold p-3">{data.feature}</div>
                            <div className="grid grid-cols-2 items-center border border-solid border-gray-300 bg-white">
                                <div className="flex p-3 justify-center items-center border-r border-solid border-gray-300">{renderCell(data.gallabox, data.gallaboxIcon)}</div>
                                <div className="flex p-3 justify-center items-center">{renderCell(data.competitor, data.competitorIcon)}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </>
    );
};

export default ComparisonTableV2;
